<template>
  <div class="entity-details">
    <ViewHeader
      :title="entityId"
      compact
    />
    <div class="d-flex align-items-center justify-content-between mb-3">
      <MzTabs v-model="currentTab" :tabs="tabs"/>
    </div>
    <div v-if="currentTab === 'Synthèse' && synthese" class="d-flex flex-column w-60">
      <CRow class="mt-3">
        <CCol col="4">
          <Text size="p5" class="mb-3 ml-3">Cycle</Text>
        </CCol>
        <CCol col="3">
          <Text size="p5" class="mb-3 ml-2">Statut</Text>
        </CCol>
        <CCol col="2">
          <Text size="p5" class="mb-3 ml-2">Nombre de tickets</Text>
        </CCol>
        <CCol col="3" />
      </CRow>
      <hr style="border-bottom: 1px solid grey"/>
      <CRow>
        <CCol col="12">
          <CycleStatus
            v-for="cycle in synthese"
            :title="cycle.name"
            :status="checkCycleStatus(cycle.tests)"
            :cycle="CYCLES_LOOKUP[cycle.name]"
            :number-of-tickets="cycle.number_of_tickets"
          >
            <CRow
              v-for="test in cycle.tests"
              class="justify-content-between align-items-center mb-2 custom-margin-right"
            >
              <CCol col="4">
                <Text size="p6" class="ml-7">
                  <CLink
                    :to="{name: 'Cycle Details', query: {tab: test.code}, params: {...$route.params, cycle: CYCLES_LOOKUP[cycle.name]}}"
                    style="color: inherit; text-decoration: none">
                    {{ test.code }} - {{ test.name }}
                  </CLink>
                </Text>
              </CCol>
              <CCol col="3">
                <div
                  class="
                    d-flex
                    flex-row
                    align-items-center
                    justify-content-start
                  "
                >
                  <div
                    class="ra-tag custom-margin-left"
                    :class="test.status ? 'success' : 'danger'"
                  >
                    {{ test.status ? "OK" : "KO" }}
                  </div>
                </div>
              </CCol>
              <CCol col="2">
                <Text size="p6" class="ml-7">
                  {{ test.number_of_tickets || "-" }}
                </Text>
              </CCol>
              <CCol col="3" />
            </CRow>
          </CycleStatus>
        </CCol>
      </CRow>
    </div>
    <EntityFormEdit
      v-else-if="currentTab === 'Fiche société'"
      preview
    />
    <div v-else-if="tables[currentTab]" :key="`tables_${currentTab}`">
      <table-component
        default-order-by="account"
        ref="tabTable"
        :url="API_ENDPOINTS.lazyTable"
        :lazy-table="tables[currentTab].result?.['0'] || tables[currentTab].result"
        :headings="COMMON_HEADINGS"
        :hidden-columns="getHiddenColumns"
        :export-visible-columns-only="false"
        displayButtonsRow
      >
        <template #export_btns>
          <template v-if="$refs.tabTable">
            <CButton
              class="ra-button"
              color="warning"
              size="sm"
              v-if="isGeneratingFile"
              v-c-tooltip="{
                content: 'Preparing file',
                placement: 'top-end'
              }"
            >
              Generating File <i class="fas fa-spinner spin-animation ml-2"/>
            </CButton>
            <CButton
              v-else
              class="ra-button"
              color="secondary"
              size="sm"
              @click="generateThenDownloadExport"
              v-c-tooltip="{
                content: 'Download export file',
                placement: 'top-end'
              }"
            >
              Export data
            </CButton>
          </template>
        </template>
        <template
          #man_comment_bg="{ row }"
        >
          <RaInput
            v-if="currentTab === tabs[1]"
            :modelValue="comments[currentTab]?.[row['aggregation_key']]?.['man_comment_bg']"
            :disabled="disabled"
            filter-input
            size="sm"
            @change="
                (newValue, isUpdated) =>
                  handleCommentInput(
                    row,
                    newValue,
                    isUpdated,
                  )
              "
          />
        </template>
      </table-component>
    </div>
    <EntityFormEdit
      v-else-if="currentTab === 'Fiche société'"
      preview
    />
  </div>
</template>

<script>
import EntityFormEdit from "./EntityFormEdit";
import {ActiveUser} from "@/services/user";
import CycleStatus from "@/components/CycleStatus";
import {ClosingFileService} from "@/services/closing_file";
import {API_ENDPOINTS} from "@/constants/common";
import {CYCLES_LOOKUP} from "@/constants/cycle";
import {COMMON_HEADINGS, COLUMNS_META} from "@/constants/closingFileTables";
import {downloadResponseFile} from "@/utils/request";

export default {
  name: "EntityDetails",
  components: {
    CycleStatus,
    EntityFormEdit,
  },
  data() {
    return {
      COMMON_HEADINGS,
      COLUMNS_META,
      API_ENDPOINTS,
      CYCLES_LOOKUP,
      entityId: "",
      quarter_name: "",
      tabs: [
        "Synthèse",
        "Balance générale",
        "Fiche société",
      ],
      currentTab: null,
      selectedRows: [],
      synthese: null,
      tables: {
        "Balance générale": {},
      },
      comments: {},
      isGeneratingFile: false
    };
  },
  computed: {
    isAdministrator() {
      return ActiveUser.isAdministrator();
    },
    getHiddenColumns() {
      return this.currentTab === "Balance générale" ? [
        'entity_code', 'account_root_1', 'account_root_2', 'account_root_3', 'account_root_4', 'aggregation_key'
      ] : []
    },
  },
  async mounted() {
    this.currentTab = this.$route.query?.tab || "Synthèse";
    this.entityId = this.$route.params.entity_id;
    this.quarter_name = this.$route.params.quarter_name;
    this.fetchTableData();
    this.synthese = await ClosingFileService.getSyntheseTable(
      this.entityId,
      this.quarter_name,
    )
    await this.getCommentInput();
  },
  methods: {
    async generateThenDownloadExport() {
      this.isGeneratingFile = true
      await this.$refs.tabTable.generateLazyTableExport()
      await this.downloadExport()
    },
    async handleCommentInput(
      row,
      newValue,
      isUpdated = false,
    ) {
      if (!isUpdated) return
      row['man_comment_bg'] = newValue;
      await ClosingFileService.updateManualControl(
        row,
        this.tabs[1],
        this.entityId,
        this.quarter_name,
        this.tabs[1],
      );
      await this.getCommentInput();
    },
    async getCommentInput() {
      this.comments = await ClosingFileService.getManualControl(
        this.tabs[1],
        this.entityId,
        this.quarter_name,
      )
    },
    async downloadExport() {
      const export_columns = this.$refs.tabTable.columnsToDisplay.map((key) => COMMON_HEADINGS[key]);
      if (this.$refs.tabTable.lazyTableExportExists && this.$refs.tabTable.lazyTableExportFilename) {
        const response = await ClosingFileService.downloadWithComments(
          this.tabs[1],
          this.entityId,
          this.quarter_name,
          this.$refs.tabTable.lazyTableExportFilename,
          export_columns,
        );
        if (response.res.status !== 200) {
          alert(response.data.detail);
          this.isGeneratingFile = false
          return
        }
        await downloadResponseFile(response.res);
        this.isGeneratingFile = false
        return
      }
      setTimeout(() => this.downloadExport(), 1000);
    },
    async triggerEntityCalculations(entity_code) {
      const task_id = await ClosingFileService.runEntityCalculations(entity_code, this.quarter_name)
    },
    async fetchTableData() {
      await Object.keys(this.tables).forEach(async (tableName) => {
        this.tables[tableName].result = await ClosingFileService.getTestTables(
          tableName,
          this.entityId,
          this.quarter_name,
        );
      });
    },
    checkCycleStatus(tests) {
      return tests.every((v) => v.status);
    },
  },
};
</script>
<style lang="scss">
.custom-margin-right {
  margin-right: 10px !important;
}

.custom-margin-left {
  margin-left: 10px !important;
}
.entity-details {
  .VueTables {
    max-height: calc(100vh - 405px) !important;
  }
}

.entity-details {
  .card {
    margin-bottom: 0;
  }

  .test-title {
    font-weight: normal;
    font-size: 16px;
  }
}

.ra-table .buttons-row {
  display: flex !important;
  justify-content: flex-end;
}

.number-of-records {
  display: none !important;
}
</style>
